import { useRef, useEffect, useState } from 'react';
import Container from '@ui/container/container';
import HoverCard from '@ui/cards/hover-card/hover-card';
import PreHoverContent from '@ui/cards/hover-card/pre-hover-content';
import HoverContent from '@ui/cards/hover-card/hover-content';
import Button from '@ui/buttons/button/button';
import Text from '@ui/text/text/text';
import { useAppSelector } from '@/store/hooks';
import { getWorkingProject, selectProjectThemePack, selectProjectThemeMode } from '@/store/slices/project/selectors';
import { campaignTemplatesMap } from '../../templates';
import { CampaignTemplate } from '@/connection/campaigns/fetch-campaign-templates';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import getThemeColorsCssMixVariable from '@/utils/colors/get-theme-color-css-mix-variable';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';

const TEMPLATE_WIDTH = (520 + 40 + 40) * 1.25;

interface SuiteCardProps {
  template: CampaignTemplate;
  onPreview: (template: CampaignTemplate) => void;
}

export default function SuiteCard({ template, onPreview }: SuiteCardProps) {
  const event = useAppSelector(getWorkingProject);
  const theme = useAppSelector(selectProjectThemePack);
  const themeMode = useAppSelector(selectProjectThemeMode);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(([entry]) => {
      setScale(entry.contentRect.width / TEMPLATE_WIDTH);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const TemplateComponent = campaignTemplatesMap.get(template.uuid);

  return (
    <Container className="flex-column gap-12" data-component="SuiteCard" flex="1">
      <HoverCard type="fade" width="100%" aspectRatio="16 / 9" containerClassName="radius-12">
        <PreHoverContent>
          <div
            ref={containerRef}
            style={{ position: 'relative', overflow: 'hidden', height: '100%', width: '100%', borderRadius: '12px' }}
          >
            {event && theme && TemplateComponent && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: TEMPLATE_WIDTH,
                  transformOrigin: 'top left',
                  transform: `scale(${scale})`,
                  pointerEvents: 'none',
                }}
              >
                <TemplateComponent
                  event={event}
                  theme={theme}
                  mode={themeMode ?? undefined}
                  // uncomment to render with variables for easier exporting to other repos
                  // calendarLinks={{
                  //   iCal: '${icalLink}',
                  //   outlook: '${outlookLink}',
                  //   google: '${googleLink}',
                  // }}
                  // customizations={{
                  //   subject: '',
                  //   title: '${title}',
                  //   description: '${description}',
                  //   cta: '${cta}',
                  //   startIntro: '${startIntro}',
                  //   salutation: '${salutation}',
                  //   headingFont: 'var(--heading-font)',
                  //   bodyFont: 'var(--body-font)',
                  //   headingImage: '${headingImage}',
                  //   mainImage: '${mainImage}',
                  //   headingTextColor: 'var(--heading-text-color)',
                  //   bodyTextColor: 'var(--body-text-color)',
                  //   backgroundColor: 'var(--background-color)',
                  //   accentColor: 'var(--accent-color)',
                  //   secondaryAccentColor: 'var(--secondary-accent-color)',
                  //   secondaryBackgroundColor: 'var(--secondary-background-color)',
                  //   secondaryImage: '${secondaryImage}',
                  //   containerColor: 'var(--container-color)',
                  //   buttonTextColor: 'var(--button-text-color)',
                  // }}
                />
              </div>
            )}
          </div>
        </PreHoverContent>

        <HoverContent backgroundColor="rgba(var(--gray-1100-rgb), 0.5)">
          <Container className="flex jc-center ai-center" height="100%" width="100%">
            <Button
              className="L1"
              backgroundColor={getThemeColorCSSVariable()}
              hoverBackgroundColor={getThemeColorsCssMixVariable()}
              color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
              onClick={() => onPreview(template)}
              borderRadius="100px"
              padding="12px 32px"
            >
              <Text
                color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
                className="L1"
              >
                Preview
              </Text>
            </Button>
          </Container>
        </HoverContent>
      </HoverCard>

      <Container maxWidth="100%">
        <Text className="H5" clamp={1} design="GlobalIcoText2">
          {template.title || template.category}
        </Text>
      </Container>
    </Container>
  );
}
